import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { TransformedFile } from '../hooks/useReferenceFiles/useReferenceFilesTypes';
import * as styles from './FileList.module.scss';
import DeleteFileButton from './DeleteFileButton';
import {
  Badge,
  Button,
  Icon,
  IconList,
  Link,
  Loading,
  StructuredList,
  StructuredListItem,
  StructuredListSecondaryActions,
  Text,
} from 'components/core';
import { Inline } from 'components/layout';

const uploadedTimeAgo = (file: TransformedFile) => {
  if (!file.createdAt) return;
  return formatDistanceToNow(new Date(file.createdAt), { addSuffix: true });
};

const primaryIcon = (file: TransformedFile) => {
  if (file.uploadError) {
    return <Icon icon={IconList.light.faFileLines} />;
  }

  if (!file.uploadComplete) {
    return <Loading type="primary" />;
  }

  return <Icon icon={IconList.light.faFileLines} />;
};

const displayName = (file: TransformedFile) => file.uploadFileName ?? file.url;

type FileListProps = {
  makeFilePrimary: (fileId: string | number) => void;
  deleteFile: (file: TransformedFile) => void;
  unsetDocumentUrl: () => void;
  files: TransformedFile[];
  documentUrl?: string;
};

export default function FileList({
  files,
  documentUrl,
  makeFilePrimary,
  unsetDocumentUrl,
  deleteFile,
}: FileListProps) {
  return (
    <StructuredList>
      {files.map((file) => (
        <StructuredListItem
          key={file.id}
          icon={primaryIcon(file)}
          primaryAction={
            (file.uploadComplete || file.uploadError) && (
              <DeleteFileButton
                fileName={file.uploadFileName}
                handleDelete={() => {
                  deleteFile(file);
                }}
              />
            )
          }
        >
          <div className={styles.LinkWrapper}>
            {file.uploadComplete ? (
              <>
                {file.url && (
                  <Link
                    to={file.url}
                    external
                    variant="primary"
                    className={styles.FullTextLink}
                  >
                    <Text size="sm" component="span">
                      {displayName(file)}
                    </Text>
                  </Link>
                )}
                {!file.url && (
                  <Text size="sm" component="span">
                    {displayName(file)}
                  </Text>
                )}
                <Inline alignX="left" spacing={1}>
                  <Text size="xs">Uploaded by {file.uploadedBy}</Text>
                  <Text>·</Text>
                  <Text size="xs" variant="weak">
                    {uploadedTimeAgo(file)}
                  </Text>
                </Inline>
              </>
            ) : (
              <>
                <Text size="sm" className={styles.FullTextLink}>
                  {displayName(file)}
                </Text>
                <Text size="sm" variant="weak">
                  {file.uploadError ? file.uploadError : 'Uploading...'}
                </Text>
              </>
            )}
          </div>
          <StructuredListSecondaryActions>
            {file.isPrimary && (
              <Badge variant="solid" type="primary">
                Primary
              </Badge>
            )}
            {file.uploadComplete && !file.isPrimary && (
              <Button
                variant="outline"
                type="neutral"
                size="sm"
                onClick={() => {
                  if (file.id) {
                    makeFilePrimary(file.id);
                  }
                }}
              >
                Make primary
              </Button>
            )}
          </StructuredListSecondaryActions>
        </StructuredListItem>
      ))}
      {documentUrl && (
        <StructuredListItem
          icon={<Icon icon={IconList.light.faLinkSimple} />}
          primaryAction={
            <DeleteFileButton fileName="url" handleDelete={unsetDocumentUrl} />
          }
        >
          <div className={styles.LinkWrapper}>
            <Link
              to={documentUrl}
              external
              variant="primary"
              className={styles.FullTextLink}
            >
              <Text size="sm" component="span">
                {documentUrl}
              </Text>
            </Link>
          </div>
        </StructuredListItem>
      )}
    </StructuredList>
  );
}
